import { twMerge } from 'tailwind-merge';
import { type ClassValue, clsx } from 'clsx';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { PricingConfig, ProductType, Target } from '../types';

const STORAGE_KEY = 'persist:auth';

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export const sortArray = (key: string, order: string) => {
  return function (a: Record<string, any>, b: Record<string, any>) {
    if (typeof a[key] === 'string' && typeof b[key] === 'string') {
      return order === 'desc' ? b[key].localeCompare(a[key]) : a[key].localeCompare(b[key]);
    } else {
      return order === 'desc' ? b[key] - a[key] : a[key] - b[key];
    }
  };
};

export const filterItems = (array: any[], scope: string, filterText: string) => {
  const filtered = array.filter(
    (item) => item[scope] && item[scope].toLowerCase().includes(filterText.toLowerCase()),
  );
  return filtered;
};

export const generalSearch = (array: any[], searchTerm: string, keys = ['']) => {
  const lowerCaseTerm = searchTerm.toLowerCase();

  return array.filter((item) => {
    if (keys.length === 0) {
      return Object.values(item).some((value) => {
        if (Array.isArray(value)) {
          return value.some((element) => element.toString().toLowerCase().includes(lowerCaseTerm));
        }
        return value && value.toString().toLowerCase().includes(lowerCaseTerm);
      });
    }

    return keys.some((key) => {
      const value = item[key];
      if (Array.isArray(value)) {
        return value.some((element) => element.toString().toLowerCase().includes(lowerCaseTerm));
      }
      return value && value.toString().toLowerCase().includes(lowerCaseTerm);
    });
  });
};

export const filterByDateRange = (
  data: any[],
  key: string,
  startDate: string | Date,
  endDate: string | Date,
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error('Invalid start or end date');
  }

  return data.filter((item) => {
    const itemDate = new Date(item[key]);
    return itemDate >= start && itemDate <= end;
  });
};

export const saveInlocalStorage = (item: string, value: any) => {
  const existingData = localStorage.getItem(STORAGE_KEY);
  if (existingData) {
    const parsedData = JSON.parse(existingData);
    parsedData[item] = value;
    const updatedData = JSON.stringify(parsedData);
    localStorage.setItem(STORAGE_KEY, updatedData);
  }
};

export const retrieveStorageData = () => {
  const localStorageData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');

  return localStorageData;
};

export const validateEmail = (email: string): string | null => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email) return 'Email is required.';
  if (!emailRegex.test(email)) return 'Please enter a valid email address.';
  return null;
};

export const verifyPassword = (
  password: string,
): {
  valid: boolean;
  error?: string;
} => {
  if (typeof password !== 'string') {
    return { valid: false, error: 'Password must be a string.' };
  }
  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters.' };
  }
  if (password.length > 64) {
    return { valid: false, error: 'Password must be no more than 64 characters.' };
  }
  // No other composition rules enforced
  return { valid: true };
};

export const validatePhoneNumber = (
  input: string,
  defaultCountry?: CountryCode,
): boolean | string => {
  const trimmed = (input || '').trim();
  if (!trimmed) {
    return 'Please enter a valid phone number.';
  }

  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : parsePhoneNumberFromString(trimmed, defaultCountry);

  if (!parsed || !parsed.isValid()) {
    return 'Please enter a valid phone number.';
  }
  return true;
};

export const formatDateTime = (dateInput: string | number | Date | null | undefined): string => {
  if (!dateInput) {
    return '';
  }

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  return date.toLocaleString('en-US', options);
};

export const formatObjective = (objective: string): string => {
  return objective
    ?.toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getMarketPricing = (
  pricingConfig: PricingConfig,
  state: string,
  product: ProductType,
  subtype: string = 'Standard MVA',
) => {
  let tier = 'D';

  for (const [tierKey, states] of Object.entries(pricingConfig.geoTiers)) {
    if (states.includes(state)) {
      tier = tierKey;
      break;
    }
  }

  const base = pricingConfig.baseRates[product]?.[tier] || { floor: 0, suggested: 0 };
  const multiplier = pricingConfig.multipliers[subtype] || 1.0;

  return {
    floor: Math.round(base.floor * multiplier),
    suggested: Math.round(base.suggested * multiplier),
    tier,
    stateCode: state,
  };
};

export const getAggregatedPricing = (
  pricingConfig: PricingConfig,
  targets: Target[],
  product: ProductType,
  subtype: string = 'Standard MVA',
) => {
  if (targets.length === 0) {
    return getMarketPricing(pricingConfig, 'D', product, subtype);
  }

  const pricingResults = targets.map((target) => getMarketPricing(pricingConfig, target.state, product, subtype));

  return pricingResults.reduce((prev, current) => (prev.floor > current.floor ? prev : current));
};
